/**
 * The print form of the report: A4, Velox brand, DE, self-hosted fonts.
 *
 * It renders the SAME `<Report>` component the screen does, with
 * `variant="print"`. That is the structural guarantee behind "nothing on paper
 * that isn't in the on-screen report": there is no second template to drift.
 * The print variant only takes things AWAY — the buttons, the links, the
 * founder-internal cost line — and adds a title block and page furniture.
 *
 * Two ways in (see `print.ts` for the full contract):
 *   · injected — `window.__BAUPILOT_PRINT__` carries report/run/diff; no fetch,
 *     no session, no 401 at render time. This is what the PDF export uses.
 *   · fetched  — a human opening /lauf/:runId/bericht/druck in their browser.
 *
 * There is deliberately no summary verdict anywhere on this page.
 */
import { useEffect, useMemo } from 'react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { LoadingBlock } from '@/components/LoadingBlock'
import { Wordmark } from '@/components/Wordmark'
import { Report } from '@/features/report/ReportView'
import {
  PRINT_MARGIN,
  PRINT_READY_ATTR,
  footerTemplate,
  headerTemplate,
  printDate,
  type InjectedPrintPayload,
} from '@/features/report/print'
import { I18nProvider, useI18n } from '@/i18n'
import { useAsync } from '@/lib/useAsync'
import type { GemeindePack, Run, RunDiff, RunReport } from '@/api/types'

/**
 * The export renders in German. The locale provider is nested on purpose: the
 * print route forces DE regardless of what the reader had selected, because the
 * paper artefact is the German one the brief specifies.
 */
export function PrintReport({
  runId,
  injected = null,
}: {
  runId?: string
  injected?: InjectedPrintPayload | null
}) {
  const locale = injected?.locale === 'en' ? 'en' : 'de'
  return (
    <I18nProvider initialLocale={locale}>
      {injected ? (
        <PrintSheet
          report={injected.report}
          run={injected.run ?? null}
          diff={injected.diff ?? null}
          packs={(injected as { packs?: GemeindePack[] }).packs ?? null}
          generatedAt={injected.generated_at ?? null}
        />
      ) : (
        <FetchedPrintSheet runId={runId ?? ''} />
      )}
    </I18nProvider>
  )
}

/** The human path: the ordinary authenticated client, same as the screen report. */
function FetchedPrintSheet({ runId }: { runId: string }) {
  const { t } = useI18n()
  const report = useAsync<RunReport>((signal) => api.report(runId, signal), [runId])
  const run = useAsync<Run>((signal) => api.run(runId, signal), [runId])
  const packs = useAsync<GemeindePack[]>((signal) => api.gemeinden(undefined, signal), [])
  const parentRunId = run.data?.parent_run_id ?? null
  const diff = useAsync<RunDiff | null>(
    (signal) => (parentRunId ? api.runDiff(runId, undefined, signal) : Promise.resolve(null)),
    [runId, parentRunId],
  )

  if (report.loading) return <LoadingBlock className="p-8" />
  if (report.error || !report.data)
    return (
      <div className="p-8">
        <ErrorNote
          message={`${t('report.loadError')} ${report.error ?? ''}`}
          onRetry={report.reload}
          retryLabel={t('common.retry')}
        />
      </div>
    )

  return (
    <PrintSheet
      report={report.data}
      run={run.data}
      diff={diff.data}
      packs={packs.data}
      generatedAt={null}
    />
  )
}

function PrintSheet({
  report,
  run,
  diff,
  packs,
  generatedAt,
}: {
  report: RunReport
  run?: Run | null
  diff?: RunDiff | null
  packs?: GemeindePack[] | null
  generatedAt?: string | null
}) {
  const { t, locale } = useI18n()
  const date = useMemo(
    () => printDate(generatedAt ?? run?.finished_at ?? run?.created_at ?? null),
    [generatedAt, run?.finished_at, run?.created_at],
  )
  const caseLabel = report.case_id ?? report.dossier_id
  const documentTitle = `${t('app.name')} ${t('report.title')} — ${caseLabel} · ${report.gemeinde} · ${date}`

  // Hand the exporter its readiness flag and its page furniture, once the page
  // is on screen AND the self-hosted fonts have loaded — a PDF taken before the
  // fonts land is laid out in a fallback face.
  useEffect(() => {
    let cancelled = false
    document.title = documentTitle
    window.__BAUPILOT_PRINT_META__ = {
      contract: 1,
      ready: false,
      runId: report.run_id,
      documentTitle,
      locale,
      caseLabel,
      gemeinde: report.gemeinde,
      zone: report.zone,
      date,
      headerTemplate: headerTemplate({
        caseLabel,
        gemeinde: report.gemeinde,
        gemeindeLabel: t('report.gemeinde'),
      }),
      footerTemplate: footerTemplate({
        date,
        dateLabel: t('print.date'),
        pageLabel: t('print.page'),
        ofLabel: t('common.of'),
        attribution: `${t('app.name')} ${t('app.attribution')}`,
      }),
      margin: { ...PRINT_MARGIN },
    }

    const ready = () => {
      if (cancelled) return
      if (window.__BAUPILOT_PRINT_META__) window.__BAUPILOT_PRINT_META__.ready = true
      document.documentElement.setAttribute(PRINT_READY_ATTR, '1')
    }

    const fontsReady = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
      ?.ready
    if (typeof fontsReady?.then === 'function') void fontsReady.then(ready, ready)
    else ready()

    return () => {
      cancelled = true
      document.documentElement.removeAttribute(PRINT_READY_ATTR)
    }
  }, [caseLabel, date, documentTitle, locale, report.gemeinde, report.run_id, report.zone, t])

  return (
    // `<main>` rather than a bare div: the print page has no application shell,
    // so without it the whole document sits outside any landmark (axe: region,
    // landmark-one-main). The title block is a plain div for the same reason —
    // a second `<header>` would read as a second banner.
    <main data-testid="print-sheet" className="print-sheet bg-white text-ink">
      <div
        data-testid="print-header"
        className="mb-8 flex flex-col gap-4 border-b border-border pb-6 break-inside-avoid"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Wordmark />
          <span className="text-[12px] text-muted">{t('app.tagline')}</span>
        </div>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-1 text-[12px]">
          <HeadItem label={t('report.case')} value={caseLabel} />
          <HeadItem label={t('report.gemeinde')} value={report.gemeinde} />
          <HeadItem label={t('report.zone')} value={report.zone} />
          <HeadItem label={t('print.date')} value={date} />
          <HeadItem label={t('run.id')} value={report.run_id} />
          {report.regime ? <HeadItem label={t('report.regime')} value={report.regime} /> : null}
        </dl>
      </div>

      <Report report={report} run={run} diff={diff} packs={packs} variant="print" />

      <p data-testid="print-footnote" className="mt-10 border-t border-border pt-4 text-[11px] text-muted">
        {t('print.footnote')}
      </p>
    </main>
  )
}

function HeadItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="num text-ink-2">{value}</dd>
    </div>
  )
}
